import React, { useCallback, useEffect, useMemo, useState } from "react"
import { gql, useApolloClient } from "@apollo/client"
import AsyncSelect from "react-select/async"

const REPRESENTATIVE_PAGE_SIZE = 20

export const ALL_INCIDENT_PARTY_REPRESENTATIVES = gql`
  query AllIncidentPartyRepresentatives($cursor: String, $first: Int, $searchTerm: String) {
    allIncidentPartyRepresentatives(first: $first, after: $cursor, searchTerm: $searchTerm) {
      edges {
        node {
          id
          name
          businessName
          phone
          email
          address
          role
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

export const formatRepresentativeOption = (node) => {
  if (!node?.id) return null

  const businessName = node.businessName || ""
  const name = node.name || ""

  return {
    value: node.id,
    label: businessName && name ? `${businessName} - ${name}` : businessName || name || "Representative",
    name: node.name,
    businessName: node.businessName,
    phone: node.phone,
    email: node.email,
    address: node.address,
    role: node.role,
  }
}

const dedupeRepresentativeOptions = (options = []) => {
  const seen = new Set()
  return options.filter((option) => {
    if (!option?.value || seen.has(option.value)) return false
    seen.add(option.value)
    return true
  })
}

export function RepresentativeSelect({ value, onChange, placeholder = "Select Representative", currentOption = null, extraOptions = [], isClearable = true, isDisabled = false }) {
  const apolloClient = useApolloClient()
  const [defaultOptions, setDefaultOptions] = useState([])
  const [selectedOptionState, setSelectedOptionState] = useState(null)

  const allKnownOptions = useMemo(() => (
    dedupeRepresentativeOptions([
      ...extraOptions,
      currentOption,
      selectedOptionState,
      ...defaultOptions,
    ])
  ), [currentOption, defaultOptions, extraOptions, selectedOptionState])

  const selectedOption = useMemo(() => {
    return allKnownOptions.find((option) => option?.value === value) || null
  }, [allKnownOptions, value])

  const fetchRepresentativeOptions = useCallback(async (searchTerm = "") => {
    const { data } = await apolloClient.query({
      query: ALL_INCIDENT_PARTY_REPRESENTATIVES,
      variables: {
        first: REPRESENTATIVE_PAGE_SIZE,
        searchTerm: searchTerm || null,
      },
      fetchPolicy: "network-only",
    })

    return dedupeRepresentativeOptions(
      data?.allIncidentPartyRepresentatives?.edges?.map(({ node }) => formatRepresentativeOption(node)) || []
    )
  }, [apolloClient])

  useEffect(() => {
    let isMounted = true

    fetchRepresentativeOptions("").then((options) => {
      if (!isMounted) return
      setDefaultOptions(options)
    }).catch(() => {
      if (!isMounted) return
      setDefaultOptions([])
    })

    return () => {
      isMounted = false
    }
  }, [fetchRepresentativeOptions])

  useEffect(() => {
    if (!value) {
      setSelectedOptionState(null)
      return
    }

    const matchingOption = allKnownOptions.find((option) => option?.value === value)
    if (matchingOption) {
      setSelectedOptionState(matchingOption)
    }
  }, [allKnownOptions, value])

  const mergedDefaultOptions = useMemo(() => (
    allKnownOptions
  ), [allKnownOptions])

  const loadOptions = useCallback(async (inputValue) => {
    const fetchedOptions = await fetchRepresentativeOptions(inputValue)
    return dedupeRepresentativeOptions([...extraOptions, currentOption, ...fetchedOptions])
  }, [currentOption, extraOptions, fetchRepresentativeOptions])

  return (
    <AsyncSelect
      className="bos-custom-select"
      classNamePrefix="bos-select"
      cacheOptions
      defaultOptions={mergedDefaultOptions}
      loadOptions={loadOptions}
      value={selectedOption}
      onChange={(option) => {
        setSelectedOptionState(option || null)
        onChange(option ? option.value : null, option || null)
      }}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
    />
  )
}
